import React from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { makePrioStyles } from '../../theme/utils';

const useStyles = makePrioStyles((theme) => ({
  welcomeContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '60vh',
    padding: theme.spacing.large,
    backgroundColor: theme.colors.application.background.default,
    borderRadius: theme.borderRadius.regular,
    border: `1px solid ${theme.colors.application.border}`,
    textAlign: 'center',
    maxWidth: '900px',
    margin: '0 auto',
  },
  heroSection: {
    marginBottom: theme.spacing.large,
  },
  heroIcon: {
    fontSize: '64px',
    color: theme.colors.base.primary.default,
    marginBottom: theme.spacing.regular,
    display: 'block',
  },
  heroTitle: {
    fontSize: '28px',
    fontWeight: 700,
    color: theme.colors.application.typography.default,
    marginBottom: theme.spacing.small,
    lineHeight: 1.2,
  },
  heroSubtitle: {
    fontSize: '16px',
    color: theme.colors.application.typography.muted,
    marginBottom: theme.spacing.large,
    lineHeight: 1.5,
    maxWidth: '700px',
    margin: `0 auto ${theme.spacing.large}px auto`,
  },
  featuresGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: theme.spacing.regular,
    marginBottom: theme.spacing.large,
    width: '100%',
  },
  featureCard: {
    padding: theme.spacing.regular,
    backgroundColor: theme.colors.application.background.light,
    borderRadius: theme.borderRadius.regular,
    border: `1px solid ${theme.colors.application.border}`,
    textAlign: 'center',
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: `0 4px 12px ${theme.colors.application.overlay.default}`,
      borderColor: theme.colors.base.primary.default,
    },
  },
  featureIcon: {
    fontSize: '32px',
    color: theme.colors.base.primary.default,
    marginBottom: theme.spacing.small,
    display: 'block',
  },
  featureTitle: {
    fontSize: '14px',
    fontWeight: 600,
    color: theme.colors.application.typography.default,
    marginBottom: theme.spacing.small,
  },
  featureDescription: {
    fontSize: '12px',
    color: theme.colors.application.typography.muted,
    lineHeight: 1.4,
  },
  stepsSection: {
    width: '100%',
    marginBottom: theme.spacing.large,
  },
  stepsTitle: {
    fontSize: '20px',
    fontWeight: 600,
    color: theme.colors.application.typography.default,
    marginBottom: theme.spacing.regular,
  },
  stepsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.regular,
  },
  stepItem: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing.regular,
    backgroundColor: theme.colors.application.background.light,
    borderRadius: theme.borderRadius.regular,
    border: `1px solid ${theme.colors.application.border}`,
    textAlign: 'left',
  },
  stepNumber: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    backgroundColor: theme.colors.base.primary.default,
    color: theme.colors.base.white.default,
    fontSize: '14px',
    fontWeight: 600,
    marginRight: theme.spacing.regular,
    flexShrink: 0,
  },
  stepContent: {
    flex: 1,
  },
  stepTitle: {
    fontSize: '14px',
    fontWeight: 600,
    color: theme.colors.application.typography.default,
    marginBottom: '4px',
  },
  stepDescription: {
    fontSize: '12px',
    color: theme.colors.application.typography.muted,
    lineHeight: 1.4,
  },
  ctaSection: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing.regular,
  },
  ctaButton: {
    minWidth: '200px',
  },
  helpText: {
    fontSize: '12px',
    color: theme.colors.application.typography.muted,
    fontStyle: 'italic',
  },
  noProjectsWarning: {
    marginBottom: theme.spacing.regular,
    maxWidth: '600px',
  },
}));

interface TaskHubWelcomeProps {
  availableProjectsCount: number;
  onGetStarted?: () => void;
  className?: string;
}

interface FeatureItem {
  iconProp: [string, string];
  titleKey: string;
  descriptionKey: string;
}

interface StepItem {
  titleKey: string;
  descriptionKey: string;
}

const TaskHubWelcome: React.FC<TaskHubWelcomeProps> = ({
  availableProjectsCount,
  onGetStarted,
  className,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();

  // Define features data
  const features: FeatureItem[] = [
    {
      iconProp: ['fal', 'chart-pie'],
      titleKey: 'taskHub:welcome.features.analytics.title',
      descriptionKey: 'taskHub:welcome.features.analytics.description',
    },
    {
      iconProp: ['fal', 'filter'],
      titleKey: 'taskHub:welcome.features.filtering.title',
      descriptionKey: 'taskHub:welcome.features.filtering.description',
    },
    {
      iconProp: ['fal', 'table'],
      titleKey: 'taskHub:welcome.features.overview.title',
      descriptionKey: 'taskHub:welcome.features.overview.description',
    },
    {
      iconProp: ['fal', 'users'],
      titleKey: 'taskHub:welcome.features.collaboration.title',
      descriptionKey: 'taskHub:welcome.features.collaboration.description',
    },
  ];

  // Define steps data
  const steps: StepItem[] = [
    {
      titleKey: 'taskHub:welcome.steps.select.title',
      descriptionKey: 'taskHub:welcome.steps.select.description',
    },
    {
      titleKey: 'taskHub:welcome.steps.execute.title',
      descriptionKey: 'taskHub:welcome.steps.execute.description',
    },
    {
      titleKey: 'taskHub:welcome.steps.analyze.title',
      descriptionKey: 'taskHub:welcome.steps.analyze.description',
    },
    {
      titleKey: 'taskHub:welcome.steps.manage.title',
      descriptionKey: 'taskHub:welcome.steps.manage.description',
    },
  ];

  return (
    <div className={`${classes.welcomeContainer} ${className || ''}`}>
      {/* Hero Section */}
      <div className={classes.heroSection}>
        <FontAwesomeIcon
          icon={['fal', 'rocket']}
          className={classes.heroIcon}
        />
        <h1 className={classes.heroTitle}>{t('taskHub:welcome.title')}</h1>
        <p className={classes.heroSubtitle}>{t('taskHub:welcome.subtitle')}</p>
      </div>

      {/* Features Grid */}
      <div className={classes.featuresGrid}>
        {features.map((feature, index) => (
          <div key={index} className={classes.featureCard}>
            <FontAwesomeIcon icon={['fal', 'star']} />
            <h3 className={classes.featureTitle}>{t(feature.titleKey)}</h3>
            <p className={classes.featureDescription}>
              {t(feature.descriptionKey)}
            </p>
          </div>
        ))}
      </div>

      {/* Getting Started Steps */}
      <div className={classes.stepsSection}>
        <h2 className={classes.stepsTitle}>
          {t('taskHub:welcome.gettingStarted')}
        </h2>
        <div className={classes.stepsContainer}>
          {steps.map((step, index) => (
            <div key={index} className={classes.stepItem}>
              <div className={classes.stepNumber}>{index + 1}</div>
              <div className={classes.stepContent}>
                <div className={classes.stepTitle}>{t(step.titleKey)}</div>
                <div className={classes.stepDescription}>
                  {t(step.descriptionKey)}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default TaskHubWelcome;
